/* eslint-disable no-restricted-imports */
import { Box } from "@chakra-ui/react";
import { FieldArrayRenderProps, useFormikContext } from "formik";
import { useMemo } from "react";
import { RiArrowUpDownLine } from "react-icons/ri";
import { ResponsiveButton } from "src/components/Buttons/ResponsiveButton";
import { SearchableMenuPopover } from "src/components/Popovers/SearchableMenuPopover";
import { HighlightedText } from "src/components/Text/HighlightedText";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { FormikType } from "./formik";

type AnswerOptionItem = {
  type: "AnswerOption";
  key: string;
  label: string;
  index: number;
};

type Props = {
  optionId: string;
  optionIndex: number;
  arrayHelpers: FieldArrayRenderProps;
  gridColumnStart?: string;
  gridRowStart?: string;
  isDisabled?: boolean;
};

export const MoveAnswerOptionPopover: React.FC<Props> = ({
  optionId,
  optionIndex,
  arrayHelpers,
  gridColumnStart,
  gridRowStart,
  isDisabled,
}) => {
  const formik = useFormikContext<FormikType>();
  const options = formik.values.options;

  const items = useMemo(() => {
    const availableOptions = options
      .map((option, index) => ({
        type: "AnswerOption" as const,
        key: option.id,
        label: option.label || `Answer ${index + 1}`,
        index,
      }))
      .filter((option) => option.key !== optionId);

    if (availableOptions.length === 0) {
      return [];
    }

    return [
      {
        group: "Answer Options",
        items: availableOptions,
      },
    ];
  }, [optionId, options]);

  const toast = useAvelaToast();

  // Don't show the popover if there's only one option or no other options to move before
  if (options.length <= 1) {
    return null;
  }

  return (
    <SearchableMenuPopover<AnswerOptionItem>
      renderItem={(item: AnswerOptionItem, keyword) => {
        return (
          <Box as="span">
            <HighlightedText invert text={item.label} keyword={keyword} />
          </Box>
        );
      }}
      title="Move before answer"
      items={items}
      popoverTrigger={
        <ResponsiveButton
          hideLabelWhen="<50rem"
          aria-label="Move answer"
          leftIcon={<RiArrowUpDownLine />}
          variant="outline"
          colorScheme="gray"
          flexShrink={0}
          gridColumnStart={gridColumnStart}
          gridRowStart={gridRowStart}
          isDisabled={isDisabled}
        >
          Move answer
        </ResponsiveButton>
      }
      onItemSelected={(selectedOption) => {
        toast.closeAll();
        // Move the current option to before the selected option
        const currentOption = options[optionIndex];
        const targetIndex = selectedOption.index;

        // Remove the current option from its position
        arrayHelpers.remove(optionIndex);

        // Calculate the new index after removal
        const newIndex =
          targetIndex > optionIndex ? targetIndex - 1 : targetIndex;

        // Insert the option at the new position
        arrayHelpers.insert(newIndex, currentOption);
        toast({
          title: "Answer moved",
        });
      }}
    />
  );
};
